import { useAtom } from 'jotai';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from 'react';
import { toastAtom } from '~/atoms/toastAtom';
import { useRegisterSW } from 'virtual:pwa-register/react';

export const PWAUpdatePrompt = () => {
  const [toastCurrent] = useAtom<Toast | null>(toastAtom);
  const updateToastShown = useRef(false);
  const offlineToastShown = useRef(false);
  const [currentVersion, setCurrentVersion] = useState<string>('');
  const addNewWidgetsRef = useRef<HTMLInputElement | null>(null);
  const resetLayoutRef = useRef<HTMLInputElement | null>(null);

  // Fetch current version on mount
  useEffect(() => {
    fetch('/version.json')
      .then((res) => res.json())
      .then((data) => setCurrentVersion(data.version))
      .catch(() => setCurrentVersion('unknown'));
  }, []);

  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      console.log('SW Registered:', r);
    },
    onRegisterError(error) {
      console.log('SW registration error', error);
    },
  });

  useEffect(() => {
    if (offlineReady && toastCurrent && !offlineToastShown.current) {
      offlineToastShown.current = true;
      toastCurrent.show({
        severity: 'success',
        summary: 'App Ready',
        detail: 'App is ready to work offline',
        life: 5000,
      });
    }
  }, [offlineReady, toastCurrent]);

  useEffect(() => {
    if (needRefresh && toastCurrent && !updateToastShown.current) {
      updateToastShown.current = true;
      toastCurrent.show({
        severity: 'info',
        summary: 'Update Available',
        detail: (
          <div className="flex flex-column gap-2">
            <span>A new version is available</span>
            {currentVersion && (
              <small className="text-500">Current: v{currentVersion}</small>
            )}
            <div className="flex flex-column gap-2">
              <div className="flex align-items-center gap-2 p-2 bg-blue-50 border-round">
                <input
                  type="checkbox"
                  id="add-new-widgets-checkbox"
                  ref={addNewWidgetsRef}
                  defaultChecked={true}
                  className="cursor-pointer"
                />
                <label
                  htmlFor="add-new-widgets-checkbox"
                  className="cursor-pointer text-sm"
                >
                  Add new widgets to all dashboards
                </label>
              </div>
              <div className="flex align-items-center gap-2 p-2 bg-blue-50 border-round">
                <input
                  type="checkbox"
                  id="reset-layout-checkbox"
                  ref={resetLayoutRef}
                  defaultChecked={false}
                  className="cursor-pointer"
                />
                <label
                  htmlFor="reset-layout-checkbox"
                  className="cursor-pointer text-sm"
                >
                  Reset layout for all views
                </label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                label="Update Now"
                icon="pi pi-refresh"
                size="small"
                className="w-full"
                onClick={async () => {
                  try {
                    // Save the update timestamp and checkbox states to localStorage
                    const shouldAddWidgets =
                      addNewWidgetsRef.current?.checked ?? true;
                    const shouldResetLayout =
                      resetLayoutRef.current?.checked ?? false;

                    if (shouldAddWidgets) {
                      const updateTimestamp = Date.now().toString();
                      localStorage.setItem(
                        'pwa-update-add-widgets-version',
                        updateTimestamp,
                      );
                      // Clear any existing processed dashboards list for new update
                      localStorage.removeItem('pwa-processed-dashboards');
                    } else {
                      // User opted out, clear any pending updates
                      localStorage.removeItem('pwa-update-add-widgets-version');
                      localStorage.removeItem('pwa-processed-dashboards');
                    }

                    if (shouldResetLayout) {
                      const updateTimestamp = Date.now().toString();
                      localStorage.setItem(
                        'pwa-update-reset-layout-version',
                        updateTimestamp,
                      );
                      // Clear any existing processed dashboards list for layout reset
                      localStorage.removeItem(
                        'pwa-processed-dashboards-layout',
                      );
                    } else {
                      // User opted out, clear any pending layout resets
                      localStorage.removeItem(
                        'pwa-update-reset-layout-version',
                      );
                      localStorage.removeItem(
                        'pwa-processed-dashboards-layout',
                      );
                    }

                    await updateServiceWorker(true);
                    setNeedRefresh(false);
                    updateToastShown.current = false; // Reset for next update
                  } catch (error) {
                    console.error('Update error:', error);
                    // Force page reload if update fails
                    window.location.reload();
                  }
                }}
              />
            </div>
            <small className="text-orange-500 font-semibold">
              ⚠️ Update required to continue using the app
            </small>
          </div>
        ),
        life: 0, // Don't auto-hide
        sticky: true,
        closable: false, // Prevent dismissing the toast
      });
    }
  }, [
    needRefresh,
    toastCurrent,
    updateServiceWorker,
    setNeedRefresh,
    setOfflineReady,
    currentVersion,
  ]);

  return null; // No UI needed, uses centralized toast system
};
